import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Represent a generic state store, you want to extend from this class to create a new
 * child state store for your module instead of using this class directly, then implement
 * the abstract method `StateStore.getInitialState` to return the initial state of the store
 */
export abstract class StateStore<T> {

  private readonly _stateChangeNotifiers = new Map<string, BehaviorSubject<unknown>>();

  constructor() {
    this._initialize(this.getInitialState());
  }

  abstract getInitialState(): T;

  private _initialize(defaultState: T) {
    for (const [key, value] of Object.entries(defaultState)) {
      const notifier = new BehaviorSubject<any>(value);
      this._stateChangeNotifiers.set(key, notifier);
      notifier.next(value);
    }
  }

  /**
   * Perform a partial update of the states in this store
   *
   * @param newState The new partial states
   */
  update<K extends keyof T>(newState: Pick<T, K>) {
    for (const [k, v] of Object.entries(newState)) {
      this._stateChangeNotifiers.get(k).next(v);
    }
  }

  /**
   * Pick a state from the store with the given name
   *
   * @param key The name of the state
   * @returns
   */
  select<K extends keyof T>(key: K) {
    return this._stateChangeNotifiers.get(key as string).asObservable() as Observable<T[K]>;
  }

  /**
   * Return the current value of the given state in the store
   *
   * @param key The name of the state
   * @returns
   */
  valueOf<K extends keyof T>(key: K): T[K] {
    return this._stateChangeNotifiers.get(key as string).value as T[K];
  }

}
